<template>
  <v-navigation-drawer v-if="auth_status" v-model="visibility" color="#F6F5F5" width="235" floating>
    <TransferDeviceDataDialog />

    <!-- Select device -->
    <template v-slot:prepend>
      <v-card rounded="0" color="#F6F5F5" class="half-1px-line-bottom pb-6 px-4 pt-0" flat>
        <v-card-subtitle class="left-bar-text pb-1 pl-0" style="color: #1b1b1b">{{ t('$vuetify.__General.device') }}</v-card-subtitle>
        <div>
          <v-autocomplete
            ref="device_select"
            content-class="device-select"
            density="compact"
            variant="outlined"
            hide-details
            auto-select-first
            menu-icon="mdi-chevron-down"
            :menu-props="{ bottom: true, offsetY: true }"
            :items="device_list"
            item-title="description"
            item-value="id"
            :custom-filter="filterDeviceByDescription"
            :filter-keys="DEVICE_FILTER_KEYS"
            :no-data-text="t('$vuetify.__LeftBar.no_device_found')"
            :placeholder="t('$vuetify.__LeftBar.search_device')"
            @focus="highlightDeviceSearchText"
            @click="highlightDeviceSearchText"
            @update:modelValue="selectDevice"
            v-model="selected_device"
          >
            <!-- <template v-slot:item="{ props: select_props, item }">
              <v-list-item v-bind="select_props" :title="item.raw.description">
                <template v-slot:title>
                  <v-hover v-slot="{ isHovering, props: hover_props }">
                    <v-row no-gutters align="center" v-bind="hover_props">
                      <span> {{ item.raw.description }} </span>
                      <v-spacer></v-spacer>
                      <v-tooltip location="top" content-class="tooltip-top tooltip-color" :text="t('$vuetify.__LeftBar.transfer_tooltip_text')">
                        <template v-slot:activator="{ props: tooltip_props }">
                          <v-btn
                            v-show="isHovering"
                            color="#1B65F2"
                            size="small"
                            icon="mdi-database-plus"
                            variant="text"
                            @click.stop="store.commit('INIT_TRANSFER_DEVICE_DATA_DIALOG', item)"
                            v-bind="tooltip_props"
                          >
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </v-row>
                  </v-hover>
                </template>
              </v-list-item>
            </template> -->
          </v-autocomplete>
        </div>
      </v-card>
    </template>

    <!-- Router -->
    <v-list density="compact" nav class="router-list mt-6" aria-label="leftbar-navigation-list">
      <div v-for="route in routes" :key="route.path" class="px-1">
        <v-list-item :to="route.path" link :class="{ 'nav-active': checkCurrentRoute(route.path) }" style="height: 48px">
          <v-row no-gutters class="pl-2" align="center">
            <v-icon class="nav-icon" :class="{ 'nav-active': checkCurrentRoute(route.path) }">{{ route.icon }}</v-icon>

            <v-list-item-content>
              <v-list-item-title class="left-bar-text pl-4" :class="{ 'nav-active': checkCurrentRoute(route.path) }">
                {{ t(`$vuetify.__Router.${route.path.slice(1)}`) }}
              </v-list-item-title>
            </v-list-item-content>
          </v-row>
        </v-list-item>
      </div>
    </v-list>

    <!-- Action buttons -->
    <template v-slot:append>
      <v-card v-if="can_submit || can_backup" rounded="0" color="#F6F5F5" class="half-1px-line-top pb-6 px-8 pt-6">
        <v-btn
          v-if="can_submit"
          variant="outlined"
          rounded
          block
          class="text-capitalize px-4 py-3"
          @click="store.dispatch('getApprovalStatus', { device_id: selected_device, device_name: description })"
        >
          <v-icon size="18" class="pr-1">mdi-send-check</v-icon>{{ t('$vuetify.__LeftBar.submit_changes') }}
        </v-btn>

        <v-btn v-if="can_backup" variant="outlined" rounded block class="text-capitalize mt-4 px-4 py-3" @click="store.dispatch('downloadBackup')">
          <v-icon size="18" class="pr-1">mdi-send-check</v-icon>{{ t('$vuetify.__LeftBar.backup_now') }}
        </v-btn>

        <!-- Notify password dialog -->
        <v-dialog v-model="back_up.visibility" persistent max-width="300px">
          <v-card class="pa-6">
            <v-row no-gutters class="card-title">{{ t('$vuetify.__LeftBar.backup_hint') }}</v-row>
            <v-row no-gutters align="center" class="py-6">
              <v-col cols="auto" class="grey-title-text">{{ back_up_password }}</v-col>
              <!-- <v-spacer></v-spacer> -->
              <v-col cols="auto" class="pl-2">
                <v-btn @click="copyPassword()" small icon color="#828282"><v-icon size="20">mdi-content-copy</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-spacer></v-spacer>
              <v-btn @click="back_up.visibility = false" class="text-capitalize white--text px-3" color="#1b65f2" rounded>
                {{ t('$vuetify.__General.done') }}
              </v-btn>
            </v-row>
          </v-card>
        </v-dialog>
      </v-card>
    </template>
  </v-navigation-drawer>
</template>

<script setup>
import TransferDeviceDataDialog from '@/components/Dialogs/TransferDeviceDataDialog'
import { filterDeviceByDescription } from './device_filter'

import { computed, ref } from 'vue'

import { useLocale } from 'vuetify'
const { t } = useLocale()
import { useStore } from 'vuex'
const store = useStore()
import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()

const back_up_password = ref('PcNByoxKd9x6QY1')
const device_select = ref(null)

// Search the device name only. Vuetify has no default for filter-keys, and without this it also matches on the internal
// item's type ('item'), value (the device id) and stringified props/raw — typing '1' would surface a device whose id is 12
// but whose name has no '1' in it. Hoisted out of the template so it keeps one identity: useFilter reads this prop inside
// a watchEffect, so a fresh array literal per render would re-run the filter every time the left bar re-renders.
const DEVICE_FILTER_KEYS = ['title']

const device_list = computed(() => store.state.auth.device_list)
const description = computed(() => device_list.value.find((device) => device.id === selected_device.value)?.description)
const selected_device = computed({
  get() {
    return store.state.auth.selected_device
  },
  set(value) {
    store.commit('SET_AUTH_SELECTED_DEVICE', value)
  }
})
const auth_status = computed(() => store.state.auth.status)
// const user_info = computed(() => store.state.auth.user_info)
const role = computed(() => store.state.auth.privileges.role)
const visibility = computed({
  get() {
    return store.state.leftbar.visibility
  },
  set(value) {
    store.commit('SET_LEFTBAR_VISIBILITY', value)
  }
})
const back_up = computed(() => store.state.leftbar.backup)
const routes = computed(() => {
  const page_access = store.state.auth.page_access
  const leftbar_routes = store.state.leftbar.routes
  if (page_access === 'all') return leftbar_routes
  return leftbar_routes.filter((route) => page_access.includes(route.path))
})

const can_submit = computed(() => {
  if (store.state.auth.privileges.privileges) {
    const privileges = store.state.auth.privileges.privileges.device[selected_device.value]
    const can_approve = privileges?.includes('Device.PR.Create') || privileges?.includes('Device.All')
    return can_approve
  } else return false
})
const can_backup = computed(() => {
  if (store.state.auth.privileges.privileges) {
    const privileges = store.state.auth.privileges.privileges.device[selected_device.value]
    const can_backup = privileges?.includes('Device.Content.Backup')
    return can_backup
  } else return false
})

const checkCurrentRoute = (currentRoute) => {
  return route.path.split('/')[1] === currentRoute.split('/')[1]
}

// On focus Vuetify puts the current device's name back into the field, so a click leaves the caret mid-word and the next
// keystroke splices the query into that name ('amb cf1 (Pcf1roduction)') instead of searching. Highlighting the text makes
// the first keystroke replace it. Bound to click as well as focus because the click's mouseup collapses any selection made
// during focus, and deferred because Vuetify has not written the name into the field yet on the first click of a session.
// setTimeout rather than requestAnimationFrame: rAF does not fire at all while a tab is hidden.
// isPristine is Vuetify's "user has not typed yet" flag — it keeps a re-click from wiping a query in progress.
const highlightDeviceSearchText = () => {
  setTimeout(() => {
    if (!device_select.value?.isPristine) return
    device_select.value.$el?.querySelector('input')?.select()
  })
}

const updateSelectedDevice = () => {
  const current_page = route.path
  if (current_page === '/dashboard' || current_page === '/setting') {
    store.dispatch('fetchDashboardSummary')
    // Refresh the Botisfy funnel for the newly-selected device too, so it can't keep showing the previous device's metrics. Independent dispatch keeps it fail-soft ([L-2]).
    store.dispatch('fetchBotisfyFunnel')
  } else {
    if (role.value === 'Operator') router.push({ path: '/setting' })
    else router.push({ path: '/dashboard' })
  }
}

// Vuetify keeps the field focused after a pick, so highlight the new name too — otherwise typing straight after choosing
// a device appends to it ('Wan Chai Officezzz') instead of starting a fresh search.
const selectDevice = () => {
  updateSelectedDevice()
  highlightDeviceSearchText()
}

const copyPassword = () => {
  navigator.clipboard.writeText(back_up_password.value).then(
    () => {
      store.commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Copied to clipboard', icon: 'check', timeout: '2000' })
    },
    () => {
      store.commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Could not copy clipboard', icon: 'close', timeout: '2000' })
    }
  )
}
</script>

<style scoped>
/* VAutocomplete's root carries .v-autocomplete but not .v-select, so the device picker needs both selectors. */
.v-select,
.v-autocomplete {
  background-color: #ffffff;
}

.navActive {
  background-color: #1b65f2;
  color: white !important;
}

.nav-icon {
  background-color: transparent;
  color: black;
}

.left-bar-text {
  font-family: 'Nunito Sans' !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  line-height: 24px !important;
}

/* device select */
:deep(.v-select > .v-input__control > .v-field > .v-field__field),
:deep(.v-autocomplete > .v-input__control > .v-field > .v-field__field),
.select-device-list-text {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #1b1b1b;
}

.v-list .v-list-item,
.v-list .v-list-item::before {
  border-radius: 16px !important;
}

.v-list-item {
  padding: 0 2px 0 12px !important;
}

.username {
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  color: #1b1b1b;
}
</style>
